/**
 * 商户门户 API 边界（live）
 * 正式前缀：/merchant/api/v1
 *
 * FE-E2b 薄壳化：公共核心下沉 @shared/api/createClient 工厂；本文件保留
 * merchant 端配置、类型与端特有分支（3417 PENDING 商户全局引导 / 异步 mock 上传），
 * 导出面与行为不变（FE-E2a 契约测试 client.contract.test.ts 锁定）。
 */
import { createClient } from '@shared/api/createClient';

export const DEFAULT_MERCHANT_ID = 20;

/**
 * 统一请求结果（US-T2-2 结构化改造：失败分支增可选 `code`——后端 Result 信封业务码
 * 原文透传，如 AUTH_403 / 4506 / 1008；HTTP 层错误（网络断开/非 JSON 体）无 code。
 * traceId 为后端信封追踪码（系统级错误时 error 已附加，字段供页面自行展示）。
 * 可选成员为非破坏性扩展：既有 `const { ok, data } = res` / `res.error` 消费方零改动。
 */
export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; code?: string; error: string; traceId?: string };

/** live-only（阻断级约定延续：生产构建禁止产出纯 mock dist）：mock 回调保留但恒关 */
export const USE_MOCK = false;

/** 会话写入后广播，供 RequireSetup / Layout 重读 me 标志 */
export const SESSION_CHANGED_EVENT = 'allinks-session';

// ================= 会话与领域视图类型（对齐后端 DTO） =================

/** 认证渠道（对齐后端 AuthChannel 枚举字面量） */
export type AuthChannel = 'ADMIN' | 'AGENT' | 'MERCHANT' | 'CARDHOLDER';

/** 当前会话身份视图（对齐后端 PortalSessionView record） */
export type PortalSessionView = {
  subjectId?: number;
  channel?: AuthChannel;
  principal?: string;
  roles?: string[];
  permissions?: string[];
  merchantId?: string;
  agentId?: number;
  cardholderId?: number;
  emailVerified?: boolean;
  emailMasked?: string;
  mustChangePassword?: boolean;
  mfaSetupRequired?: boolean;
  /** OTP 宽限剩余秒；无宽限为 0（对齐后端 PortalSessionView） */
  otpGraceRemainingSeconds?: number;
  /** 代登录会话标识（契约 §0.4：me 响应末位字段，R75） */
  impersonated?: boolean;
};

/** 运行时菜单节点（对齐后端 MenuNodeView record） */
export type MenuNodeView = {
  id: string;
  title: string;
  path?: string | null;
  permission?: string | null;
  children?: MenuNodeView[];
};

/** 商户会话：作用域 id = merchantId（来自 me.merchantId） */
export type MerchantSession = {
  accessToken?: string;
  refreshToken?: string;
  principal?: string;
  /** /auth/me 结果（登录后写入；拉取失败置 null） */
  me?: PortalSessionView | null;
  /** /auth/menus 结果（登录后写入；拉取失败置 null） */
  menus?: MenuNodeView[] | null;
  /** accessToken 预估过期时间戳（ms）；缺省表示未知 */
  expiresAt?: number;
  /** 作用域商户号（登录后由 me.merchantId 写入；十进制字符串，禁 Number 往返） */
  merchantId?: string;
  /** 运营代登录会话标识（/auth/portal-login 换取；顶层持久化，响应无 refreshToken 不可续期 D3） */
  impersonated?: boolean;
};

// ================= 3417 全局引导（qa-fix-n1n10 US-001 · FIX-001） =================

/**
 * 建号极简：PENDING 商户访问非白名单业务 API 时后端返回 3417（AUTH_SCOPE_PENDING_ONBOARDING）。
 * 统一在此识别并引导至认证内 KYB 自助页 /merchant/onboarding-application
 * （挂 RequireAuth→RequireSetup 内，保证「验邮 → 强制改密 → KYB」顺序）。
 * KYB 页自身端点在 3417 白名单内（/onboarding-application* 特例），不会产生重定向循环。
 */
const PENDING_ONBOARDING_CODE = '3417';
const ONBOARDING_APPLICATION_PATH = '/merchant/onboarding-application';

/** 由 App 注入的 3417 引导回调（navigate('/merchant/onboarding-application')）；未注入时硬跳兜底 */
let onPendingOnboarding: (() => void) | null = null;

export function setPendingOnboardingHandler(fn: (() => void) | null) {
  onPendingOnboarding = fn;
}

/** 3417 → 引导 KYB 自助页；已在该页则不重复跳转（并发请求竞态防重入） */
function redirectToOnboardingApplication() {
  if (typeof window !== 'undefined'
      && window.location.pathname.startsWith(ONBOARDING_APPLICATION_PATH)) {
    return;
  }
  if (onPendingOnboarding) {
    onPendingOnboarding();
  } else if (typeof window !== 'undefined') {
    window.location.assign(ONBOARDING_APPLICATION_PATH);
  }
}

// ================= 工厂实例（端差异参数化） =================

const core = createClient<MerchantSession>({
  apiBase: {
    dev: '/merchant/api/v1',
    prod: 'https://allinks-api.swapsle.com/merchant/api/v1',
  },
  sessionKey: 'allinks_merchant_session',
  loginPath: '/login',
  // merchant 无 dev 伪角色/回退身份头（镜像须知 #9，三端无 dev 回退头）
  customHeaderMode: 'spread',
  // merchant 幂等键回退形态：idem-{ts}-{random36}（带随机后缀，保持原行为）
  jsonKeyGen: 'ts-random36',
  uploadKeyGen: 'ts-random36',
  // FeeItem deleteOverride 等写接口要求 Idempotency-Key
  deleteIdempotencyKey: true,
  // 3417：PENDING 商户访问受限业务 API → 统一引导至 KYB 自助页（JSON + multipart 双通道）
  onBusinessError: (code) => {
    if (code === PENDING_ONBOARDING_CODE) {
      redirectToOnboardingApplication();
    }
  },
});

export const MERCHANT_API_BASE = core.apiBase;

export function merchantApiUrl(path: string): string {
  return core.apiUrl(path);
}

export function loadSession(): MerchantSession | null {
  return core.loadSession();
}

export function saveSession(s: MerchantSession) {
  core.saveSession(s);
}

export function clearSession() {
  core.clearSession();
}

export function setAuthFailureHandler(fn: (() => void) | null) {
  core.setAuthFailureHandler(fn);
}

/** 清会话 + 跳登录：refresh 失败 / 非过期 401 / 主动登出均走此路径 */
export function signOutLocal() {
  core.signOutLocal();
}

// ================= 公共 API（保持既有签名，向后兼容） =================

export async function merchantGet<T>(path: string, mock: () => T): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  return core.fetchWithRefresh<T>('GET', path, undefined);
}

export async function merchantPost<T>(
  path: string,
  body: unknown,
  mock: () => T,
  opts?: { skipAuth?: boolean; headers?: Record<string, string>; idempotencyKey?: string },
): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  return core.fetchWithRefresh<T>('POST', path, body, opts);
}

/** multipart 上传；沿用 merchant 401 refresh 与信封解析契约（mock 形态：支持异步回调）。 */
export async function merchantUpload<T>(
  path: string,
  form: FormData,
  mock: () => T | Promise<T>,
  opts?: { skipAuth?: boolean; headers?: Record<string, string>; idempotencyKey?: string },
): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    try {
      return { ok: true, data: await mock() };
    } catch (error) {
      return { ok: false, error: error instanceof Error ? error.message : 'mock_upload_error' };
    }
  }
  return core.upload<T>(path, form, opts);
}

/** 写操作（更新）；PUT 带 JSON body + Idempotency-Key；走 fetchWithRefresh */
export async function merchantPut<T>(
  path: string,
  body: unknown,
  mock: () => T,
  opts?: { skipAuth?: boolean; headers?: Record<string, string>; idempotencyKey?: string },
): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  return core.fetchWithRefresh<T>('PUT', path, body, opts);
}

/**
 * 写操作（局部变更）；PATCH 带 JSON body + Idempotency-Key；走 fetchWithRefresh。
 * 企业域启停端点专用（corporate-api BUD-003 / RSK-002 @PatchMapping），条件头
 * （X-Confirm）经 opts.headers 注入。
 */
export async function merchantPatch<T>(
  path: string,
  body: unknown,
  mock: () => T,
  opts?: { skipAuth?: boolean; headers?: Record<string, string>; idempotencyKey?: string },
): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  return core.fetchWithRefresh<T>('PATCH', path, body, opts);
}

/** 删除 / 撤销；无 body + Idempotency-Key；走 fetchWithRefresh */
export async function merchantDelete<T>(
  path: string,
  mock: () => T,
  // 2026-09-22：补 headers（与 merchantPost/merchantPut 对齐）——PRD §9.4 敏感写需经 X-OTP 头。
  opts?: { skipAuth?: boolean; idempotencyKey?: string; headers?: Record<string, string> },
): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  return core.fetchWithRefresh<T>('DELETE', path, undefined, opts);
}
